import React, { useEffect, useContext } from 'react';

import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';

import type { Comment } from './useAllCommentsData.ts';
import { useAllCommentsData } from './useAllCommentsData.ts';
import {
	categorizeComments,
	buildDefaultCommentsContentStatusFilters,
} from './commentDataManagerUtils.ts';
import { useProcessedCommentsDataActions } from './useProcessedCommentsData.ts';

// Initiates the loading of all comment data for the specified page in the background
// This component is also responsible for processing the raw comments data and updating
// the ProcessedCommentsDataStore with categorized results using default contentStatus filters.

// Experience tracking constants
const COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE = 'comments-data-background-load';

type CommentsDataBackgroundLoaderProps = {
	contentId: string;
};

export const CommentsDataBackgroundLoader = ({ contentId }: CommentsDataBackgroundLoaderProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();
	const { setProcessedData, setError, setRefetch } = useProcessedCommentsDataActions();

	// Call the hook to load all comments - should only when content has settled and is ready so we
	// don't impact loading times
	// Always fetch with network-only to ensure we get fresh data
	const { loading, error, data, refetch } = useAllCommentsData({
		contentId,
		options: {
			skip: !contentId,
			fetchPolicy: 'network-only',
		},
	});

	// Set refetch function in store whenever it changes
	useEffect(() => {
		if (refetch) {
			setRefetch(refetch);
		}
	}, [refetch, setRefetch]);

	// Process and update the ProcessedCommentsDataStore when data changes
	// This is the ONLY place where the processed data store should be updated
	useEffect(() => {
		// Don't process data while loading
		if (loading) {
			return;
		}

		if (error) {
			// Set error state in the store
			setError(error);
			return;
		}

		// Filter out null nodes and create array (empty array if no data)
		const allComments = (data?.comments?.nodes || []).filter(
			(node: Comment | null): node is Comment => node !== null,
		);

		// Categorize comments using default contentStatus filters
		const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);
		const categorized = categorizeComments(allComments, contentStatusFilters);

		// Update the processed data store with categorized results
		// Note: refetch is set separately via setRefetch, so we don't include it here
		setProcessedData({
			allComments,
			...categorized,
			isDataProcessed: true,
		});
	}, [data, loading, error, pageMode, setProcessedData, setError]);

	// Experience tracking: Handle success/failure when loading completes
	useEffect(() => {
		if (!contentId) {
			return;
		}

		// Only track completion if we're not still loading
		if (!loading) {
			if (error) {
				// Experience failed due to error
				experienceTracker.fail({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
					error,
				});
			} else {
				// Experience succeeded - comments data loaded successfully
				experienceTracker.succeed({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
				});
			}
		}
	}, [loading, error, contentId, experienceTracker]);

	// This component only triggers the hook, doesn't render anything other than the ExperienceStart,
	// which handles being rendered multiple times with the same props gracefully
	return (
		<ExperienceStart
			name={COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE}
			attributes={{
				...getCommentsDataManagerExperimentAttributes(),
				contentId,
			}}
		/>
	);
};
